import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessagesSquare, Inbox, Users, Activity, Settings,
  SlidersHorizontal, LogOut, Menu, X, PanelLeftClose, PanelLeft,
  ChevronsUpDown, ArrowLeft, FlaskConical,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";

const GROUPS = [
  {
    title: "Workspace",
    items: [
      { id: "dashboard", to: "/app/dashboard", label: "Overview", icon: LayoutDashboard },
      { id: "conversations", to: "/app/conversations", label: "Chats", icon: MessagesSquare },
      { id: "requests", to: "/app/requests", label: "Requests", icon: Inbox },
      { id: "customers", to: "/app/customers", label: "Customers", icon: Users },
      { id: "agent-activity", to: "/app/agent-activity", label: "Activity", icon: Activity },
    ],
  },
  {
    title: "Manage",
    items: [
      { id: "edit-setup", to: "/onboarding/business", label: "Edit setup", icon: SlidersHorizontal },
      { id: "settings", to: "/app/settings", label: "Settings", icon: Settings },
    ],
  },
];

function NavItem({ item, collapsed, onClick }) {
  return (
    <NavLink
      to={item.to}
      data-testid={`nav-${item.id}`}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => `nav-link ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`}
    >
      <item.icon size={18} strokeWidth={1.6} style={{ flexShrink: 0 }} />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

function NavGroups({ collapsed, onItemClick }) {
  return (
    <nav className="flex-1 overflow-y-auto py-2">
      {GROUPS.map((g, gi) => (
        <div key={g.title} className={gi > 0 ? "mt-5" : ""}>
          {collapsed ? (
            gi > 0 && <div className="mx-3 my-2" style={{ borderTop: "1px solid var(--border)" }} />
          ) : (
            <div className="px-4 mb-1.5 mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
              {g.title}
            </div>
          )}
          {g.items.map((it) => (
            <NavItem key={it.id} item={it} collapsed={collapsed} onClick={onItemClick} />
          ))}
        </div>
      ))}
    </nav>
  );
}

function BusinessSwitcher({ collapsed }) {
  const { businesses, active, setActive } = useBusiness();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!active) return null;
  if (collapsed) {
    return (
      <div className="flex justify-center py-3" style={{ borderBottom: "1px solid var(--border)" }} title={active.name}>
        <div className="flex items-center justify-center mono text-sm" style={{ width: 30, height: 30, background: "var(--s3)", border: "1px solid var(--border-strong)", color: "var(--accent)" }}>
          {active.name?.[0]?.toUpperCase()}
        </div>
      </div>
    );
  }
  return (
    <div className="relative px-3 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
      <button data-testid="business-switcher" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        style={{ background: "var(--s2)", border: "1px solid var(--border)" }}>
        <div className="min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Workspace</div>
          <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{active.name}</div>
        </div>
        <ChevronsUpDown size={15} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
      </button>
      {open && (
        <div className="absolute left-3 right-3 z-20 mt-1" style={{ background: "var(--s3)", border: "1px solid var(--border-strong)" }}>
          <button onClick={() => { navigate("/app"); setOpen(false); }} data-testid="all-workspaces"
            className="w-full text-left px-3 py-2 text-sm nav-link flex items-center gap-2">
            <ArrowLeft size={14} /> All workspaces
          </button>
          {businesses.length > 1 && (
            <>
              <div style={{ borderTop: "1px solid var(--border)" }} />
              {businesses.map((b) => (
                <button key={b.id} onClick={() => { setActive(b.id); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm nav-link">
                  {b.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Wordmark({ small }) {
  return (
    <Link to="/" className="flex items-center gap-2.5" title="Home" data-testid="wordmark-home" style={{ cursor: "pointer" }}>
      <div style={{ width: small ? 18 : 20, height: small ? 18 : 20, background: "var(--accent)" }} />
      <span className="mono font-semibold tracking-tight" style={{ fontSize: small ? 15 : 16 }}>Wappy</span>
    </Link>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("handl_nav_collapsed") === "1");

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("handl_nav_collapsed", c ? "0" : "1");
      return !c;
    });
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: "var(--canvas)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col flex-shrink-0 transition-all duration-200"
        style={{ width: collapsed ? 68 : 244, borderRight: "1px solid var(--border)", background: "var(--s1)" }}>
        <div className={`flex items-center h-16 ${collapsed ? "justify-center px-0" : "justify-between px-4"}`} style={{ borderBottom: "1px solid var(--border)" }}>
          {!collapsed && <Wordmark onClick={() => navigate("/app")} />}
          <button onClick={toggleCollapsed} className="btn btn-ghost btn-sm" data-testid="nav-collapse-toggle"
            title={collapsed ? "Expand menu" : "Collapse menu"} style={{ padding: 6 }}>
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <BusinessSwitcher collapsed={collapsed} />
        <NavGroups collapsed={collapsed} />
        <div className={`py-3 ${collapsed ? "px-2" : "px-3"}`} style={{ borderTop: "1px solid var(--border)" }}>
          {!collapsed && <div className="text-xs mb-2 truncate mono" style={{ color: "var(--text-muted)" }}>{user?.email}</div>}
          <button className={`btn btn-sm w-full ${collapsed ? "px-0 justify-center" : ""}`} data-testid="logout-btn" onClick={doLogout} title="Sign out">
            <LogOut size={14} /> {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden flex items-center justify-between px-4 h-14 flex-shrink-0"
          style={{ borderBottom: "1px solid var(--border)", background: "var(--s1)" }}>
          <Wordmark small onClick={() => navigate("/app")} />
          <button className="btn btn-ghost btn-sm" onClick={() => setDrawer(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu size={20} />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto px-4 md:px-8 py-6">{children}</main>
      </div>

      {/* Mobile drawer */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setDrawer(false)}>
          <div className="absolute left-0 top-0 bottom-0 w-72 flex flex-col" style={{ background: "var(--s1)", borderRight: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 h-14" style={{ borderBottom: "1px solid var(--border)" }}>
              <Wordmark small onClick={() => { navigate("/app"); setDrawer(false); }} />
              <button className="btn btn-ghost btn-sm" onClick={() => setDrawer(false)} aria-label="Close menu"><X size={18} /></button>
            </div>
            <BusinessSwitcher collapsed={false} />
            <NavGroups collapsed={false} onItemClick={() => setDrawer(false)} />
            <div className="px-3 py-3" style={{ borderTop: "1px solid var(--border)" }}>
              <div className="text-xs mb-2 truncate mono" style={{ color: "var(--text-muted)" }}>{user?.email}</div>
              <button className="btn btn-sm w-full" onClick={doLogout}><LogOut size={14} /> Sign out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
